"use client";

import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { cn } from "@/lib/utils";

/**
 * <img> that falls back to `fallback` when src is empty or fails to load
 * (404 / 403 / expired URL), so a broken URL never shows a broken-image icon.
 */
export function AvatarImage({
  src,
  alt,
  title,
  className = "",
  style,
  fallback,
}: {
  src?: string;
  alt?: string;
  title?: string;
  className?: string;
  style?: CSSProperties;
  fallback: ReactNode;
}) {
  const [failed, setFailed] = useState(false);
  // Reset on src change so a new image gets a fresh chance to load.
  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={alt}
        style={style}
        referrerPolicy="same-origin"
        className={className}
        onError={() => setFailed(true)}
      />
    );
  }

  return <>{fallback}</>;
}

interface MemberAvatarProps {
  name?: string;
  email?: string;
  userId?: string;
  /** Explicit image override; otherwise resolved from the workspace directory. */
  image?: string;
  /** Classes for the circle (size / bg / text / rounding / centering). The same
   *  classes are reused for the image (sizing/rounding) and the letter. */
  className?: string;
  /** Inline styles (e.g. a dynamic fallback background colour). */
  style?: CSSProperties;
  /** Optional alt/title text; defaults to name or email. */
  title?: string;
}

/**
 * Member avatar: profile image (resolved by userId/email, or passed directly),
 * falling back to the name/email initial — including when the image fails to load.
 */
export function MemberAvatar({
  name,
  email,
  userId,
  image,
  className = "",
  style,
  title,
}: MemberAvatarProps) {
  const { getMemberImage } = useWorkspaceContext();
  const src = image || getMemberImage(userId) || getMemberImage(email);
  const label = title || name || email || "";
  const letter = (name || email || "U").charAt(0).toUpperCase();

  return (
    <AvatarImage
      src={src}
      alt={label}
      title={label}
      style={style}
      className={cn("object-cover m-0", className)}
      fallback={
        <span
          className={cn("flex items-center justify-center", className)}
          style={style}
          title={label}
        >
          {letter}
        </span>
      }
    />
  );
}
